// OSType descriptor reader.
//
// Photoshop serializes Action-Manager-style descriptors for many
// Additional Layer Information blocks (lfx2, lrFX successor, text engine,
// vector mask, etc.).  The format is documented in the Adobe Photoshop File
// Formats Specification under "Descriptor structure".
//
// A descriptor is:
//   - 4 bytes: number of items
//   - then N items, each:
//       - key:  length-prefixed 4-char-or-longer name (length 0 means
//               literal 4 bytes follow)
//       - type: 4-byte OSType tag (see OS_TYPE_* below)
//       - body: type-dependent
//
// We parse only the OSTypes needed for `lfx2`.  Unknown OSTypes throw so
// callers can decide whether to bail out of the whole descriptor (the lfx2
// parser catches and treats it as "unrecognized — skip").
//
// IMPORTANT: this file is core/ — no DOM, no Buffer.  It only uses
// BinaryReader (which itself only depends on Uint8Array / DataView).

import { BinaryReader, BinaryReaderError } from "./BinaryReader";

export class OSTypeDescriptorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OSTypeDescriptorError";
  }
}

// -- Parsed value tree ----------------------------------------------------

export type OSTypeValue =
  | { kind: "descriptor"; classId: string; name: string; items: OSTypeItems }
  | { kind: "reference"; items: OSTypeReferenceItem[] }
  | { kind: "list"; items: OSTypeValue[] }
  | { kind: "double"; value: number }
  | { kind: "unitFloat"; unit: string; value: number }
  | { kind: "string"; value: string }
  | { kind: "enumerated"; typeId: string; value: string }
  | { kind: "integer"; value: number }
  | { kind: "largeInteger"; value: number }
  | { kind: "boolean"; value: boolean }
  | { kind: "class"; classId: string; name: string }
  | { kind: "alias"; data: Uint8Array }
  | { kind: "rawData"; data: Uint8Array };

export type OSTypeReferenceItem =
  | { kind: "property"; classId: string; name: string; keyId: string }
  | { kind: "class"; classId: string; name: string }
  | { kind: "enumeratedRef"; classId: string; name: string; typeId: string; value: string }
  | { kind: "offset"; classId: string; name: string; value: number }
  | { kind: "identifier"; value: number }
  | { kind: "index"; value: number }
  | { kind: "name"; classId: string; name: string; value: string };

/** Ordered key/value items inside a descriptor. */
export type OSTypeItems = Array<{ key: string; value: OSTypeValue }>;

// -- OSType tags ----------------------------------------------------------

export const OS_TYPE = {
  Reference: "obj ",
  Descriptor: "Objc",
  List: "VlLs",
  Double: "doub",
  UnitFloat: "UntF",
  String: "TEXT",
  Enumerated: "enum",
  Integer: "long",
  LargeInteger: "comp",
  Boolean: "bool",
  GlobalObject: "GlbO",
  Class1: "type",
  Class2: "GlbC",
  Alias: "alis",
  RawData: "tdta",
} as const;

// -- Public entry points --------------------------------------------------

/**
 * Read a top-level descriptor with its leading 4-byte version field.
 * lfx2 / many text engine blocks use this framing.
 */
export function readVersionedDescriptor(r: BinaryReader): {
  version: number;
  descriptor: OSTypeValue & { kind: "descriptor" };
} {
  const version = r.readU32();
  const descriptor = readDescriptor(r);
  return { version, descriptor };
}

/** Read a descriptor body (after the OSType tag, or as a top-level body). */
export function readDescriptor(
  r: BinaryReader,
): OSTypeValue & { kind: "descriptor" } {
  const { classId, name } = readClassId(r);
  const count = r.readU32();
  // Guard against pathological item counts to keep fuzz inputs from looping
  // forever.  4M items is far more than any real PSD descriptor.
  if (count > 4_000_000) {
    throw new OSTypeDescriptorError(
      `descriptor item count ${count} is implausibly large`,
    );
  }
  const items: OSTypeItems = [];
  for (let i = 0; i < count; i++) {
    const key = readKey(r);
    const value = readValue(r);
    items.push({ key, value });
  }
  return { kind: "descriptor", classId, name, items };
}

// -- Helpers --------------------------------------------------------------

/**
 * Descriptor "name" + class ID.  The "name" is a Unicode string (typically
 * empty); the class ID is a length-prefixed 4-char OSType key (or longer
 * when length != 0).
 */
function readClassId(r: BinaryReader): { classId: string; name: string } {
  const name = r.readUnicodeString();
  const classId = readLengthPrefixedKey(r);
  return { classId, name };
}

/** Descriptor item key: same length-prefixed-key encoding as class IDs. */
function readKey(r: BinaryReader): string {
  return readLengthPrefixedKey(r);
}

/**
 * Length-prefixed key.  4-byte length: when zero, the key is exactly 4
 * ASCII bytes ("the standard OSType"); when non-zero, the key is that many
 * ASCII bytes.  This is the convention from the Adobe spec.
 */
function readLengthPrefixedKey(r: BinaryReader): string {
  const length = r.readU32();
  const n = length === 0 ? 4 : length;
  if (n > 1024) {
    throw new OSTypeDescriptorError(
      `descriptor key length ${n} is implausibly large`,
    );
  }
  return r.readAscii(n);
}

function readValue(r: BinaryReader): OSTypeValue {
  const type = r.readAscii(4);
  switch (type) {
    case OS_TYPE.Descriptor:
    case OS_TYPE.GlobalObject:
      return readDescriptor(r);
    case OS_TYPE.List: {
      const count = r.readU32();
      if (count > 4_000_000) {
        throw new OSTypeDescriptorError(
          `list item count ${count} is implausibly large`,
        );
      }
      const items: OSTypeValue[] = [];
      for (let i = 0; i < count; i++) items.push(readValue(r));
      return { kind: "list", items };
    }
    case OS_TYPE.Double:
      return { kind: "double", value: r.readDouble() };
    case OS_TYPE.UnitFloat: {
      const unit = r.readAscii(4);
      const value = r.readDouble();
      return { kind: "unitFloat", unit, value };
    }
    case OS_TYPE.String:
      return { kind: "string", value: r.readUnicodeString() };
    case OS_TYPE.Enumerated: {
      const typeId = readLengthPrefixedKey(r);
      const value = readLengthPrefixedKey(r);
      return { kind: "enumerated", typeId, value };
    }
    case OS_TYPE.Integer:
      return { kind: "integer", value: r.readI32() };
    case OS_TYPE.LargeInteger:
      // "comp" is a 64-bit integer.  We rely on readU64's safe-number guard.
      return { kind: "largeInteger", value: r.readU64() };
    case OS_TYPE.Boolean:
      return { kind: "boolean", value: r.readU8() !== 0 };
    case OS_TYPE.Class1:
    case OS_TYPE.Class2: {
      const { classId, name } = readClassId(r);
      return { kind: "class", classId, name };
    }
    case OS_TYPE.Alias: {
      const length = r.readU32();
      return { kind: "alias", data: r.readBytes(length).slice() };
    }
    case OS_TYPE.RawData: {
      const length = r.readU32();
      return { kind: "rawData", data: r.readBytes(length).slice() };
    }
    case OS_TYPE.Reference:
      return readReference(r);
    default:
      throw new OSTypeDescriptorError(
        `unknown OSType "${type}" at offset ${r.tell() - 4}`,
      );
  }
}

function readReference(r: BinaryReader): OSTypeValue {
  const count = r.readU32();
  const items: OSTypeReferenceItem[] = [];
  for (let i = 0; i < count; i++) {
    const t = r.readAscii(4);
    switch (t) {
      case "prop": {
        const { classId, name } = readClassId(r);
        const keyId = readLengthPrefixedKey(r);
        items.push({ kind: "property", classId, name, keyId });
        break;
      }
      case "Clss": {
        const { classId, name } = readClassId(r);
        items.push({ kind: "class", classId, name });
        break;
      }
      case "Enmr": {
        const { classId, name } = readClassId(r);
        const typeId = readLengthPrefixedKey(r);
        const value = readLengthPrefixedKey(r);
        items.push({ kind: "enumeratedRef", classId, name, typeId, value });
        break;
      }
      case "rele": {
        const { classId, name } = readClassId(r);
        items.push({ kind: "offset", classId, name, value: r.readI32() });
        break;
      }
      case "Idnt":
        items.push({ kind: "identifier", value: r.readI32() });
        break;
      case "indx":
        items.push({ kind: "index", value: r.readI32() });
        break;
      case "name": {
        const { classId, name } = readClassId(r);
        const value = r.readUnicodeString();
        items.push({ kind: "name", classId, name, value });
        break;
      }
      default:
        throw new OSTypeDescriptorError(
          `unknown reference item type "${t}"`,
        );
    }
  }
  return { kind: "reference", items };
}

// -- Convenience accessors ------------------------------------------------

/** Find a child item in a descriptor by key.  Returns undefined when absent. */
export function getItem(
  descriptor: OSTypeValue & { kind: "descriptor" },
  key: string,
): OSTypeValue | undefined {
  for (const item of descriptor.items) {
    if (item.key === key) return item.value;
  }
  return undefined;
}

export function asDescriptor(
  v: OSTypeValue | undefined,
): (OSTypeValue & { kind: "descriptor" }) | undefined {
  return v && v.kind === "descriptor" ? v : undefined;
}

export function asBoolean(v: OSTypeValue | undefined): boolean | undefined {
  return v && v.kind === "boolean" ? v.value : undefined;
}

export function asDouble(v: OSTypeValue | undefined): number | undefined {
  if (!v) return undefined;
  if (v.kind === "double") return v.value;
  if (v.kind === "unitFloat") return v.value;
  if (v.kind === "integer") return v.value;
  if (v.kind === "largeInteger") return v.value;
  return undefined;
}

export function asUnitFloat(
  v: OSTypeValue | undefined,
): { unit: string; value: number } | undefined {
  if (!v) return undefined;
  if (v.kind === "unitFloat") return { unit: v.unit, value: v.value };
  if (v.kind === "double") return { unit: "", value: v.value };
  return undefined;
}

export function asInteger(v: OSTypeValue | undefined): number | undefined {
  if (!v) return undefined;
  if (v.kind === "integer") return v.value;
  if (v.kind === "largeInteger") return v.value;
  if (v.kind === "double") return Math.round(v.value);
  return undefined;
}

export function asString(v: OSTypeValue | undefined): string | undefined {
  return v && v.kind === "string" ? v.value : undefined;
}

export function asEnumerated(
  v: OSTypeValue | undefined,
): { typeId: string; value: string } | undefined {
  return v && v.kind === "enumerated"
    ? { typeId: v.typeId, value: v.value }
    : undefined;
}

export function asList(
  v: OSTypeValue | undefined,
): OSTypeValue[] | undefined {
  return v && v.kind === "list" ? v.items : undefined;
}

export function asRawData(
  v: OSTypeValue | undefined,
): Uint8Array | undefined {
  return v && v.kind === "rawData" ? v.data : undefined;
}

// -- Re-export for callers that want a single import surface --------------

export { BinaryReader, BinaryReaderError };
